import {chain, compact} from 'lodash';
import {createSelector} from 'reselect';

import {logger} from '../../../../srcCommon/services/logger/logger';
import {isTeamInbox, teammateCanAccessInbox, teammateCanMoveToInbox} from '../../../helpers/modelHelpers';
import {canMoveToInbox} from '../../../helpers/models/conversationHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {AppState} from '../../appState';
import {getCanMoveToInaccessibleInbox} from '../company/companySelectors';
import {ConversationBasesSelector} from '../conversations/conversationsSelectors';
import {getDelegatedInboxIdsSet} from '../delegations/delegationsSelectors';
import {getUser} from '../teammates/teammatesSelectors';

/*
 * Inboxes.
 */

export type GetInboxes = AppParametricSelector<any, ReadonlyArray<InboxModel>>;

/** Get the map of all inboxes. */
export function getInboxes(state: AppState) {
  return state.data.inboxes.byId;
}

export function getInboxesTable(state: AppState) {
  return state.data.inboxes;
}

export type InboxIdSelector<P> = AppParametricSelector<P, number | undefined>;
type InboxSelector<P> = AppParametricSelector<P, InboxModel | undefined>;

/** Get a specific inbox by id. */
export function makeGetInbox<P>(getInboxId: InboxIdSelector<P>): InboxSelector<P> {
  return createSelector(getInboxId, getInboxes, (inboxId, inboxes) =>
    inboxId ? inboxes[inboxId] : undefined,
  );
}

export type InboxIdsSelector<P> = AppParametricSelector<P, readonly number[]>;
type InboxesSelector<P> = AppParametricSelector<P, ReadonlyArray<InboxModel>>;

/** Get a list of inboxes from a list of ids. */
export function makeGetInboxesById<P>(getInboxIds: InboxIdsSelector<P>): InboxesSelector<P> {
  return createSelector(getInboxIds, getInboxes, (inboxIds, inboxes) => {
    const selectedInboxes = inboxIds.map((id) => {
      const inbox = inboxes[id];
      if (inbox === undefined) {
        logger.warn(`Inbox was undefined when getting inboxes by ID. Inboxes IDs: `, {id});
      }
      return inbox;
    });

    return compact(selectedInboxes);
  });
}

/** Get a flat list of all inboxes, sorted by name. */
export const getFlatInboxes = createSelector(getInboxes, (inboxes) =>
  chain(inboxes)
    .values()
    .compact()
    .sortBy((i) => i.name.toLowerCase())
    .value(),
);

/** Get a flat list of inboxes belonging to our user. */
export const getUserOwnedInboxes = createSelector([getUser, getFlatInboxes], (user, inboxes) =>
  // Filter the inboxes by our user's namespace.
  inboxes.filter((i) => i.namespace === user.namespace),
);

export const getUserOwnedInboxesCount = createSelector(
  getUserOwnedInboxes,
  (inboxes) => inboxes?.length || 0,
);

/** Get a flat list of the inboxes that we particioate in. */
export const getUserParticipatingInboxes = createSelector([getUser, getFlatInboxes], (user, inboxes) =>
  inboxes.filter((i) => teammateCanAccessInbox(user, i)),
);

/** Get a flat list of other teammates' private inboxes shared with our user. */
export const getUserDelegatedInboxes = createSelector(
  [getUser, getUserParticipatingInboxes],
  (user, inboxes) =>
    inboxes.filter((i) => i.namespace !== user.namespace && isTeammateNamespace(i.namespace)),
);

/** Get a flat list of the team inboxes that we participate in. */
export const getUserTeamParticipatingInboxes = createSelector(
  [getUser, getUserParticipatingInboxes],
  (user, participatingInboxes) =>
    participatingInboxes.filter((i) => i.namespace !== user.namespace && isTeamNamespace(i.namespace)),
);

/** Get a flat list of the inboxes that we can move to. */
export const getUserMoveTargetInboxes = createSelector(
  [getCanMoveToInaccessibleInbox, getUser, getFlatInboxes, getDelegatedInboxIdsSet],
  (canMoveToInaccessibleInbox, user, inboxes, delegatedInboxIdsSet) =>
    inboxes.filter((i) => teammateCanMoveToInbox(user, i, delegatedInboxIdsSet, canMoveToInaccessibleInbox)),
);

/** Get a flat list of all team inboxes. */
export const getSharedInboxes = createSelector([getFlatInboxes], (inboxes) =>
  inboxes.filter((i) => isTeamInbox(i)),
);

export function makeGetMoveTargetInboxes(getTargetConversations: ConversationBasesSelector<any>) {
  return createSelector([getUserMoveTargetInboxes, getTargetConversations], (targetInboxes, conversations) =>
    targetInboxes.filter((inbox) => canMoveToInbox(inbox, conversations)),
  );
}

/** Get a flat list of the inboxes we can compose a discussion to (i.e. team inboxes we can move conversations to). */
export const getInternalDiscussionRecipientInboxes = createSelector(
  [getUserMoveTargetInboxes],
  (userMoveTargetInboxes) => userMoveTargetInboxes.filter((i) => isTeamInbox(i)),
);

export function makeGetInboxesByNamespace(getNamespace: AppParametricSelector<any, string>) {
  return createSelector([getNamespace, getFlatInboxes], (namespace, inboxes) =>
    inboxes.filter((i) => i.namespace === namespace),
  );
}
